import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { BellRing, RefreshCw } from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
  Badge,
  Button,
  Card,
  ConfirmDialog,
  DataTable,
  EmptyState,
  SectionLabel,
  type DataTableColumn,
} from '@wundo/ui';
import type { SkillScheduleEntry } from '@wundo/shared/userSkillOverview.ts';
import { deactivateSkillSchedule, userSkillOverviewKey } from '@/lib/users.ts';
import { formatSkillDateTime, relativeFromNow } from './skillOverviewFormat.ts';

function scheduleKindLabel(kind: SkillScheduleEntry['kind']): string {
  return kind === 'reminder' ? 'reminder' : 'check-in';
}

export interface UserSkillSchedulesProps {
  userId: string;
  schedules: SkillScheduleEntry[];
  /** The person's own timezone, which is the one the schedules fire in. */
  timezone: string;
}

export function UserSkillSchedules({
  userId,
  schedules,
  timezone,
}: UserSkillSchedulesProps) {
  const queryClient = useQueryClient();
  const [deactivating, setDeactivating] = useState<SkillScheduleEntry | null>(null);

  const deactivate = useMutation({
    mutationFn: (scheduleId: string) => deactivateSkillSchedule(scheduleId),
    onSuccess: async () => {
      setDeactivating(null);
      await queryClient.invalidateQueries({
        queryKey: userSkillOverviewKey(userId),
      });
    },
  });

  const activeSchedules = schedules.filter((schedule) => schedule.active);
  const inactiveSchedules = schedules.filter((schedule) => !schedule.active);

  return (
    <section className='flex flex-col gap-3'>
      <div className='flex items-end justify-between gap-4'>
        <div>
          <SectionLabel as='h2'>Schedules</SectionLabel>
          <p className='mt-1 text-xs text-ink-muted'>
            Reminders are one-off. Check-ins repeat. Deactivating cancels silently — the
            user isn't notified.
          </p>
        </div>
        <p className='text-xs whitespace-nowrap text-ink-muted'>
          {`${activeSchedules.length} active · ${inactiveSchedules.length} past`}
        </p>
      </div>

      {activeSchedules.length === 0 ? (
        <Card>
          <EmptyState title='No active schedules.' />
        </Card>
      ) : (
        <div className='grid grid-cols-1 gap-3 agenda:grid-cols-2'>
          {activeSchedules.map((schedule) => (
            <ScheduleCard
              key={schedule.id}
              schedule={schedule}
              timezone={timezone}
              busy={deactivate.isPending}
              onDeactivate={() => setDeactivating(schedule)}
            />
          ))}
        </div>
      )}

      {inactiveSchedules.length > 0 && (
        <Accordion>
          <AccordionItem value='past'>
            <AccordionTrigger>{`Past schedules (${inactiveSchedules.length})`}</AccordionTrigger>
            <AccordionPanel>
              <PastSchedules schedules={inactiveSchedules} timezone={timezone} />
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      )}

      <ConfirmDialog
        open={deactivating !== null}
        onOpenChange={(open) => !open && setDeactivating(null)}
        title='Deactivate schedule?'
        description={
          deactivating
            ? `Deactivate this ${scheduleKindLabel(deactivating.kind)} (${
                deactivating.skill_name ?? 'standalone'
              })? The user will not be notified.`
            : undefined
        }
        confirmLabel='Deactivate'
        cancelLabel='Cancel'
        variant='danger'
        onConfirm={async () => {
          if (deactivating) {
            await deactivate.mutateAsync(deactivating.id).catch(() => undefined);
          }
        }}
      />
    </section>
  );
}

interface ScheduleCardProps {
  schedule: SkillScheduleEntry;
  timezone: string;
  busy: boolean;
  onDeactivate: () => void;
}

function ScheduleCard({ schedule, timezone, busy, onDeactivate }: ScheduleCardProps) {
  const next = schedule.fire_at ?? schedule.next_due_at;

  return (
    <Card className='flex flex-col gap-2'>
      <div className='flex items-start justify-between gap-3'>
        <div className='flex min-w-0 items-center gap-2'>
          <Badge tone={schedule.kind === 'reminder' ? 'info' : 'accent'}>
            {schedule.kind === 'reminder' ? (
              <BellRing aria-hidden='true' className='mr-1 size-3' />
            ) : (
              <RefreshCw aria-hidden='true' className='mr-1 size-3' />
            )}
            {scheduleKindLabel(schedule.kind)}
          </Badge>
          <span
            className={
              schedule.skill_name
                ? 'truncate text-sm font-semibold text-ink'
                : 'truncate text-sm font-semibold text-ink italic'
            }
          >
            {schedule.skill_name ?? 'Standalone'}
          </span>
        </div>
        <Button variant='ghost' size='sm' disabled={busy} onClick={onDeactivate}>
          Deactivate
        </Button>
      </div>

      {schedule.kind === 'reminder' ? (
        <p className='text-sm text-ink-secondary'>{schedule.content ?? '—'}</p>
      ) : (
        <p className='font-mono text-xs text-ink-muted'>
          {schedule.rrule_human ?? schedule.rrule}
        </p>
      )}

      <div className='flex items-start gap-6 border-t border-fill pt-2 text-xs text-ink-muted'>
        <div>
          <p className='mb-0.5'>Next</p>
          <p className='font-semibold text-ink-secondary'>
            {relativeFromNow(next)}
            <span className='ml-1 font-normal text-ink-muted'>
              {` · ${formatSkillDateTime(next, timezone)} (${timezone})`}
            </span>
          </p>
        </div>
        {schedule.last_fired_at && (
          <div>
            <p className='mb-0.5'>Last</p>
            <p className='font-semibold text-ink-secondary'>
              {relativeFromNow(schedule.last_fired_at)}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

function PastSchedules({
  schedules,
  timezone,
}: {
  schedules: SkillScheduleEntry[];
  timezone: string;
}) {
  const columns: DataTableColumn<SkillScheduleEntry>[] = [
    {
      key: 'skill_name',
      header: 'Skill',
      render: (row) => row.skill_name ?? 'Standalone',
    },
    { key: 'kind', header: 'Kind', render: (row) => scheduleKindLabel(row.kind) },
    {
      key: 'last_fired_at',
      header: 'Last fired',
      nowrap: true,
      render: (row) => formatSkillDateTime(row.last_fired_at, timezone),
    },
    {
      key: 'skipped_reason',
      header: 'Skipped',
      render: (row) => row.skipped_reason ?? '—',
    },
  ];

  return (
    <DataTable<SkillScheduleEntry>
      label='Past schedules'
      columns={columns}
      rows={schedules}
      rowKey={(row) => row.id}
    />
  );
}
